import { License } from "@/models/license.model";
import { t } from "@/helpers/i18n";
import { formatDate } from "@/utils/date";

// Licencja wygasająca w tym horyzoncie jest oznaczana ostrzeżeniem
const expiringSoonDays = 30;
const dayMs = 24 * 60 * 60 * 1000;

export type LicenseTone = "ok" | "warn" | "bad" | "none";
export type LicenseState = "active" | "expiring" | "expired" | "inactive";

export interface LicenseSummary {
    tone: LicenseTone;
    text: string;
    detail: string;
    // Kolejność przy sortowaniu: najpierw klienci wymagający uwagi
    rank: number;
}

/**
 * Liczba pełnych dni do końca ważności. Główna aplikacja uznaje licencję za wygasłą, gdy minie data ważności.
 */
export function daysUntil(date: string, now: Date = new Date()): number {
    return Math.floor((new Date(date).getTime() - now.getTime()) / dayMs);
}

function isExpired(license: License, now: Date): boolean {
    return !!license.expireDate && new Date(license.expireDate).getTime() < now.getTime();
}

export function licenseState(license: License, now: Date = new Date()): LicenseState {
    if (!license.isActive) {
        return "inactive";
    }
    if (isExpired(license, now)) {
        return "expired";
    }
    if (license.expireDate && daysUntil(license.expireDate, now) <= expiringSoonDays) {
        return "expiring";
    }
    return "active";
}

export function expiringText(days: number): string {
    if (days <= 0) {
        return t("licenses.summary.expiresToday");
    }
    if (days === 1) {
        return t("licenses.summary.expiresTomorrow");
    }
    return t("licenses.summary.expiresInDays", { days });
}

function activeCountText(count: number): string {
    if (count === 1) {
        return t("licenses.summary.activeOne");
    }
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;
    const isFew = lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 12 || lastTwoDigits > 14);
    return t(isFew ? "licenses.summary.activeFew" : "licenses.summary.activeMany", { count });
}

/**
 * Podsumowanie licencji klienta do listy. Licencja dotyczy jednej maszyny (klucz maszyny),
 * więc ostrzeżenie liczy się dla maszyny, której ważność kończy się najwcześniej.
 */
export function summarizeLicenses(licenses: License[], now: Date = new Date()): LicenseSummary {
    const valid = licenses.filter(license => license.isActive && !isExpired(license, now));
    if (valid.length === 0) {
        const expired = licenses
            .filter(license => license.isActive && isExpired(license, now))
            .map(license => license.expireDate as string)
            .sort();
        if (expired.length > 0) {
            const lastValid = formatDate(expired[expired.length - 1]);
            return { tone: "bad", text: t("licenses.summary.expired"), detail: t("licenses.summary.lastValidTo", { date: lastValid }), rank: 1 };
        }
        return { tone: "none", text: t("licenses.summary.none"), detail: "", rank: 0 };
    }

    // Koniec ważności każdej maszyny: null = bezterminowo, inaczej najpóźniejsza data jej licencji
    const coverage = new Map<string, string | null>();
    for (const license of valid) {
        const current = coverage.get(license.licenseKey);
        if (current === null) {
            continue;
        }
        if (!license.expireDate) {
            coverage.set(license.licenseKey, null);
        } else if (current === undefined || license.expireDate > current) {
            coverage.set(license.licenseKey, license.expireDate);
        }
    }

    const ends = Array.from(coverage.values()).filter((end): end is string => end !== null).sort();
    const count = activeCountText(valid.length);
    if (ends.length === 0) {
        return { tone: "ok", text: count, detail: t("licenses.summary.perpetual"), rank: 3 };
    }

    const earliestEnd = ends[0];
    const days = daysUntil(earliestEnd, now);
    const until = t("licenses.summary.until", { date: formatDate(earliestEnd) });
    if (days <= expiringSoonDays) {
        return { tone: "warn", text: expiringText(days), detail: `${count} · ${until}`, rank: 2 };
    }
    return { tone: "ok", text: count, detail: until, rank: 3 };
}
